import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import RevisionTemplateForm, {
  RevisionTemplateFormData,
} from 'components/forms/revision-template/RevisionTemplateForm';
import { FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR } from 'env';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { REVISION_TEMPLATES_PAGE_PATH, TEMPLATE_EDITOR_PAGE_PATH } from 'routes/paths';

/**
 * Allows the user to create new revision template.
 */
export default function AddRevisionTemplatePage() {
  const { getAction } = useApi();
  const navigate = useNavigate();
  const createdTemplateId = useRef<number>();

  /**
   * Handles the form submission.
   */
  function onSubmit({
    name,
    slug,
    kinds,
    deviceTypeId,
    restriction,
    organizations,
    blacklistedOrganizations = [],
    supportsKep,
    singleDeviceOnly,
  }: RevisionTemplateFormData) {
    const revisionTemplateCreateAction = getAction('RevisionTemplateCreate');

    const isOrganization = restriction === 'organization';

    return revisionTemplateCreateAction({
      payload: {
        name,
        slug,
        kinds,
        deviceTypeId: Number(deviceTypeId),
        isOrganization,
        isGlobal: restriction === 'global',
        organizations: isOrganization ? organizations.map(({ organizationId }) => organizationId) : [],
        blacklistedOrganizations: isOrganization
          ? []
          : blacklistedOrganizations.map(({ organizationId }) => organizationId),
        supportsKep,
        singleDeviceOnly,
      },
    }).then(({ revisionTemplateId }) => {
      notifications.show({
        title: 'Šablóna revíznej správy bola úspešne vytvorená',
        message: `Šablóna revíznej správy ${name} bola úspešne vytvorená.`,
      });

      createdTemplateId.current = revisionTemplateId;
    });
  }

  /** Opens the saved template after the form has cleared its dirty state. */
  function onSaved() {
    if (FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR) {
      navigate(TEMPLATE_EDITOR_PAGE_PATH.insert({ revisionTemplateId: createdTemplateId.current }));
    } else {
      navigate(REVISION_TEMPLATES_PAGE_PATH.original);
    }
  }

  return (
    <DashboardLayout
      title="Pridanie novej šablóny revíznej správy"
      breadcrumbs={[
        { title: 'Šablóny revíznych správ', link: REVISION_TEMPLATES_PAGE_PATH.original },
        { title: 'Pridanie novej šablóny revíznej správy' },
      ]}
    >
      <RevisionTemplateForm
        onSubmit={onSubmit}
        onSaved={onSaved}
        primaryButtonText="Vytvoriť"
        primaryIcon={<IconPlus stroke="1.5" height={24} width={24} />}
      />
    </DashboardLayout>
  );
}
